import React, { useEffect, useState } from "react";
import "./ManagePhotos.css";
import { db, storage } from "../../components/firebase/firebase";
import {
  Button,
  Image,
  Input,
  Notification,
  Text,
  Title,
} from "../../components/ui";
import { formatDate } from "../../components/function";
import { Container, Flex, SafeArea } from "../../components/layout";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { deleteObject, ref } from "firebase/storage";
import { logEvent } from "../../utils/logger";

const MAX_DATA = 30; // Nombre max d'articles à charger

function ManagePhotos() {
  const [data, setData] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async (reset = false) => {
    setLoading(true);

    try {
      let dataQuery = query(
        collection(db, "photos"), // ✅ Utilisation correcte de collection()
        orderBy("createdAt", "desc"), // ✅ Tri par date descendante
        limit(MAX_DATA), // ✅ Limite du nombre d'articles
      );

      if (!reset && lastDoc) {
        dataQuery = query(dataQuery, startAfter(lastDoc)); // ✅ Pagination
      }

      const querySnapshot = await getDocs(dataQuery); // ✅ Récupération des documents
      const newArticles = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setData((prev) => (reset ? newArticles : [...prev, ...newArticles]));
      setLastDoc(
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null,
      );
    } catch (error) {
      logEvent("ERROR", "DB", "Failed to fetch photos", {
        error: error.message,
        stack: error.stack,
        isReset: reset,
      });
      console.error("Erreur lors du chargement des articles :", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    if (value.trim().length < 3) {
      fetchArticles(true);
      return;
    }

    try {
      const querySnapshot = await db
        .collection("photos")
        .where("name", ">=", value)
        .where("name", "<=", value + "\uf8ff")
        .get();

      setData(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      logEvent("ERROR", "DB", "Photos search failed", {
        error: error.message,
        stack: error.stack,
        searchQuery: value,
      });
      console.error("Erreur de recherche :", error);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sel) => sel !== id) : [...prev, id],
    );
  };

  const handleAllSelect = () => {
    if (selected.length === data.length) {
      setSelected([]);
    } else {
      setSelected(data.map((d) => d.id));
    }
  };

  const handleDelete = async () => {
    if (selected.length === 0) return;
    setDeleteLoading(true);

    try {
      // Récupérer toutes les références aux documents à supprimer
      const docsToDelete = await Promise.all(
        selected.map(async (id) => {
          const docRef = doc(db, "photos", id);
          const photo = await getDoc(docRef);
          return photo.exists()
            ? { id, img: photo.data().img, ref: docRef }
            : null;
        }),
      );

      // Filtrer les documents existants
      const validDocs = docsToDelete.filter((doc) => doc !== null);

      // Supprimer les images du Storage
      await Promise.all(
        validDocs.map(async ({ img }) => {
          if (img) {
            const storageRef = ref(storage, img);
            await deleteObject(storageRef).catch((err) =>
              console.warn("Erreur lors de la suppression de l'image:", err),
            );
          }
        }),
      );

      // Supprimer les documents Firestore
      await Promise.all(validDocs.map(({ ref }) => deleteDoc(ref)));

      // Mettre à jour l'état local
      setData((prev) => prev.filter((photo) => !selected.includes(photo.id)));
      setSelected([]);
      setNotification("success");
      logEvent("INFO", "DB", "Photos deleted", {
        deletedCount: validDocs.length,
        deletedIds: selected,
      });
    } catch (error) {
      logEvent("ERROR", "DB", "Photos deletion failed", {
        error: error.message,
        stack: error.stack,
        attemptedIds: selected,
        count: selected.length,
      });
      console.error("Erreur lors de la suppression :", error);
      setNotification("error");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <SafeArea className="ManagePhotos">
      {notification && (
        <Notification
          state={notification}
          msg={
            notification === "success"
              ? "Opération réussie !"
              : "Erreur lors de l'opération"
          }
          onClose={() => setNotification(null)}
        />
      )}
      <Flex type="col" gap={50}>
        <Title level="h3">Gérer les photos</Title>

        <Flex type="col" className="wrapper" gap={50}>
          <Input
            type="search"
            placeholder="Rechercher une photo"
            onChange={handleSearch}
          />

          <Container className="result">
            <Flex type="between-center">
              <Text>{data.length} photos</Text>
              <Button
                skin="red"
                onClick={handleDelete}
                disabled={selected.length === 0}
                loading={deleteLoading}
              >
                Supprimer
              </Button>
            </Flex>

            <Flex type="col" gap={20}>
              <Container className="table">
                <table>
                  <thead>
                    <tr>
                      <th>
                        <div
                          className={
                            "ctn-input" +
                            (selected.length !== 0 &&
                            selected.length === data.length
                              ? " active"
                              : "")
                          }
                          onClick={handleAllSelect}
                        >
                          <div className="check"></div>
                        </div>
                      </th>
                      <th></th>
                      <th
                        style={{
                          textAlign: "left",
                        }}
                      >
                        Nom
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          paddingRight: "35px",
                        }}
                      >
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((d, index) => (
                      <tr
                        key={index}
                        onClick={() => handleSelect(d.id)}
                        className={selected.includes(d.id) ? "active" : ""}
                      >
                        <td>
                          <div className="ctn-input">
                            <div className="check"></div>
                          </div>
                        </td>
                        <td>
                          <Image src={d.img} />
                        </td>
                        <td>{d.name}</td>
                        <td style={{ textAlign: "right" }}>
                          {formatDate(d.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Container>
              {lastDoc && (
                <Flex type="center">
                  <Button
                    text="Charger plus"
                    skin="nobg"
                    onClick={() => lastDoc && fetchArticles()}
                    loading={loading}
                  />
                </Flex>
              )}
            </Flex>
          </Container>
        </Flex>
      </Flex>
    </SafeArea>
  );
}

export default ManagePhotos;
